import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCategories } from "@/hooks/useCategories";
import { useProductionLocations } from "@/hooks/useProductionLocations";
import { useCombos, Combo } from "@/hooks/useCombos";
import { Loader2 } from "lucide-react";

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  description: z.string().optional(),
  category_id: z.string().optional(),
  production_location_id: z.string().optional(),
  price: z.coerce.number().min(0, "Preço deve ser maior ou igual a zero"),
  is_active: z.boolean(),
  requires_notes: z.boolean(),
  available_pdv: z.boolean(),
  available_table: z.boolean(),
  available_tablet: z.boolean(),
  available_delivery: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

interface ComboBasicFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  combo?: Combo | null;
  onSuccess?: (comboId: string) => void;
}

export function ComboBasicFormDialog({
  open,
  onOpenChange,
  combo,
  onSuccess,
}: ComboBasicFormDialogProps) {
  const { categories, fetchCategories } = useCategories();
  const { locations, fetchLocations } = useProductionLocations();
  const { createCombo, updateCombo } = useCombos();

  useEffect(() => {
    if (open) {
      fetchCategories();
      fetchLocations();
    }
  }, [open, fetchCategories, fetchLocations]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: "",
      production_location_id: "",
      price: 0,
      is_active: true,
      requires_notes: false,
      available_pdv: true,
      available_table: true,
      available_tablet: true,
      available_delivery: true,
    },
  });

  useEffect(() => {
    if (combo) {
      form.reset({
        name: combo.name,
        description: combo.description || "",
        category_id: combo.category_id || "",
        production_location_id: (combo as any).production_location_id || "",
        price: Number(combo.price),
        is_active: combo.is_active ?? true,
        requires_notes: (combo as any).requires_notes ?? false,
        available_pdv: combo.available_pdv ?? true,
        available_table: combo.available_table ?? true,
        available_tablet: combo.available_tablet ?? true,
        available_delivery: combo.available_delivery ?? true,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        category_id: "",
        production_location_id: "",
        price: 0,
        is_active: true,
        requires_notes: false,
        available_pdv: true,
        available_table: true,
        available_tablet: true,
        available_delivery: true,
      });
    }
  }, [combo, form, open]);

  const onSubmit = async (data: FormData) => {
    try {
      const comboData = {
        name: data.name,
        description: data.description || null,
        category_id: data.category_id || null,
        production_location_id: data.production_location_id || null,
        price: data.price,
        is_active: data.is_active,
        requires_notes: data.requires_notes,
        available_pdv: data.available_pdv,
        available_table: data.available_table,
        available_tablet: data.available_tablet,
        available_delivery: data.available_delivery,
        combo_type: 'normal', // New combos use slots system
      };

      if (combo) {
        await updateCombo.mutateAsync({ id: combo.id, ...comboData } as any);
        onOpenChange(false);
        onSuccess?.(combo.id);
      } else {
        const result = await createCombo.mutateAsync(comboData as any);
        onOpenChange(false);
        onSuccess?.(result.id);
      }
    } catch (error) {
      console.error("Erro ao salvar combo:", error);
    }
  };

  const isPending = createCombo.isPending || updateCombo.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {combo ? "Editar Combo" : "Novo Combo"}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-1">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Combo</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Combo Família" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Descrição do combo (opcional)"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="category_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Categoria</FormLabel>
                      <Select
                        value={field.value || "none"}
                        onValueChange={(value) =>
                          field.onChange(value === "none" ? "" : value)
                        }
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Nenhuma</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Base (R$)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Valor adicional fixo
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="production_location_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Local de Produção</FormLabel>
                    <Select
                      value={field.value || "none"}
                      onValueChange={(value) =>
                        field.onChange(value === "none" ? "" : value)
                      }
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Herdar do produto</SelectItem>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription className="text-xs">
                      Onde o pedido será impresso
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-3 pt-2">
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel className="font-medium">Ativo</FormLabel>
                        <FormDescription className="text-xs">
                          Combo disponível para venda
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="requires_notes"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div>
                        <FormLabel className="font-medium">Requer Observação</FormLabel>
                        <FormDescription className="text-xs">
                          Obriga informar observação ao adicionar
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2 pt-2">
                <FormLabel className="text-sm font-medium">Canais de Venda</FormLabel>
                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="available_pdv"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 rounded border p-2">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">PDV</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="available_table"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 rounded border p-2">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">QR Mesa</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="available_tablet"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 rounded border p-2">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">Tablet</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="available_delivery"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 rounded border p-2">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">Delivery</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {combo ? "Salvar" : "Criar e Configurar Slots"}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
